import React from 'react';
import {
  get,
  noop
} from 'lodash';
import {
  ELSLoggingService,
  ELSTokenServiceRegistrar
} from '@els/els-ui-common-react';
import {
  fetchCrosswalkUser,
  fetchUser
} from '../../apis/eols-user-management-service/eols-user-management-service.utilities';
import {
  CrosswalkUserDto,
  SystemType,
  UserDto
} from '../../apis/eols-user-management-service/eols-user-management-service.dtos';
import { AppContextState } from './AppProvider.component';
import {
  removeCookies,
  setAppLinkCookies
} from '../../pages/app-link-redirect/app-link-redirect.utilities';
import {
  NewRelicAnalyticsAction,
  AnalyticsActionProps,
} from '../../models/new-relic-analytics.models';
import {
  fetchAppLinkData,
  fetchHashLink,
  getExternalAppRedirectUrl,
  getReturnAppLink,
  HashLinkDto
} from '../../apis/eols-app-link/eols-app-link.utilities';
import { scrubProps } from '../../utilities/analytics.utilities';
import {
  getDefaultNewRelicActionProps,
  shouldGetNewOsmosisToken
} from './app-state-selectors.utilities';
import {
  fetchGroupedFeatureFlags,
  fetchGroupFeatureFlag,
  postGroupFeatureFlag
} from '../../apis/eols-features-api/eols-features-api.utilities';
import {
  getActiveABTestFlags,
  getRandomABTestFlavor
} from '../../utilities/featureFlag.utilities';
import {
  FeatureFlagsGroupedDto,
  FeatureFlagDto
} from '../../apis/eols-features-api/eols-features-api.dtos';
import {
  fetchCourseSection as fetchCourseSectionService,
  fetchUserCourseSections
} from '../../apis/eols-course-crud/eols-course-crud.utilities';
import {
  deleteUserHistoryByUserId,
  fetchUserHistoryByCourseByStateKey,
  postUserHistory
} from '../../apis/eols-user-crud/eols-user-crud.utilities';
import {
  CoursewareUserHistoryStateKey,
  EolsUserHistoryDto,
  EolsUserHistoryResponseDto
} from '../../apis/eols-user-crud/eols-user-crud.dtos';
import {
  ApiStatus,
  AppState
} from './app-state.types';
import { Application } from '../../apis/eols-app-link/eols-app-link.constants';
import { AppLinkOutConfig } from '../../apis/eols-app-link/eols-app-link.models';
import {
  fetchOsmosisToken,
  fetchVitalSource
} from '../../apis/sherpath-course-management-service/sherpath-course-management-service.utilities';
import {
  fetchEbookCfiSsoUrl,
  fetchEbookSsoUrl,
  fetchPostProcessJobs,
  patchFollowUpQuestion,
  adminFetchChat,
  adminFetchChatEntries,
  adminFetchChatEntry,
  adminPrompt,
  adminSearchVector,
  deleteChat,
  fetchAdminChatEeoIsbns,
  fetchChat,
  fetchChatEeoIsbns,
  fetchChatEntitlements,
  fetchChatEntries,
  fetchChatEntry,
  fetchChatEvaluations,
  fetchChatPerformance,
  fetchChats,
  fetchFollowUpQuestions,
  fetchStudentThemesStatus,
  postChat,
  postChatEntryShells,
  postChatPrompt,
  postStudentThemes,
  putChat,
  putFollowUpQuestion,
  putReferenceVideoData,
  fetchRelatedContent,
  fetchFlashcards,
  fetchGeneratedQuizQuestions,
  fetchQuizQuestions,
  createPracticeQuiz
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.utilities';
import {
  EbookCfiSsoUrlRequestDto,
  EbookSsoUrlRequestDto,
  EbookSsoUrlResponseDto,
  AdminChatIsbnBookDto,
  ChatDto,
  ChatEntitlementDto,
  ChatEntitlementTypeDto,
  ChatEntryDto,
  ChatEntryEvaluationDto,
  ChatPaginationResponseDto,
  ChatPerformanceDto,
  ChatReferenceDto,
  ChatUserRoleDto,
  ShellsDto,
  FollowUpQuestionDto,
  LlmPromptDto,
  LlmResponseDto,
  RelatedContentDto,
  StudentThemesStatusDto,
  VectorSearchDto,
  VectorSearchResponseDto,
  ContextRequestDto,
  FlashcardResponseDto,
  GeneratedQuizQuestionDto,
  PostProcessJobDto,
  PostProcessJobTypeDto,
  QuizQuestionQueryParamsDto,
  QuizQuestionListResponseDto,
  CreatePracticeQuizRequestDto,
  CreatePracticeQuizResponseDto,
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { CourseSectionDto } from '../../apis/eols-course-crud/eols-course-crud.dtos';
import { AppConstants } from './app.constants';
import {
  isInputBlocked,
  toastActiveStreamingError,
} from '../../pages/ai-chat/ai-chat.utilities';
import { ELSToastService } from '@els/els-component-toast-react';
import { fetchIpAddress } from '../../apis/iipify/ipify.service';
import {
  AdobeAnalyticsActionProps
} from '../../models/adobe-analytics.models';
import { fetchOsmosisLearnData } from '../../apis/osmosis-service/osmosis-service.utilities';
import { AppLinkData } from '../../apis/eols-app-link/eols-app-link.dtos';
import { OsmosisTokenDto } from '../../apis/sherpath-course-management-service/sherpath-course-management-service.dtos';
import { OsmosisLearnData } from '../../apis/osmosis-service/osmosis-service.dtos';
import { putRefreshToken } from '../../apis/eols-token-service/eols-token-service.apis';
import { TokenRotationResponse } from '../../types/token.types';

const fileName = 'app.state.actions.ts';
export const trackNewRelicAction = (props: {
  appStateRef: React.MutableRefObject<AppState>;
  actionProps: AnalyticsActionProps;
}): void => {
  const appState = props.appStateRef.current;
  const defaultActionProps = getDefaultNewRelicActionProps(appState);
  const actionProps = scrubProps(props.actionProps.props);
  const _props = {
    ...defaultActionProps,
    ...actionProps
  };
  ELSLoggingService.info(fileName, `New Relic PageAction: ${props.actionProps.action}, ${JSON.stringify(_props)}`);
  if (!window.newrelic) {
    return;
  }
  window.newrelic.addPageAction(props.actionProps.action, _props);
};

export const trackAdobeAction = (props: AdobeAnalyticsActionProps): void => {
  try {
    window.pageDataTracker.trackEvent(
      props.action,
      props.pageData
    );
  } catch (e) {
    noop(e);
  }
};

const requestStart = (props: { setAppState: AppContextState['setAppState'] }): void => {
  props.setAppState((prevState): AppState => {
    return {
      ...prevState,
      pendingRequestCount: prevState.pendingRequestCount + 1
    };
  });
};

const requestEnd = (props: { setAppState: AppContextState['setAppState'] }): void => {
  props.setAppState((prevState): AppState => {
    return {
      ...prevState,
      pendingRequestCount: prevState.pendingRequestCount - 1
    };
  });
};

export const handle401Error = (props: {
  appStateRef: React.MutableRefObject<AppState>;
  url: string;
  method: string;
}): void => {
  const { appStateRef, url, method } = props;
  const debugInfo = ELSTokenServiceRegistrar.getDebugInfo();
  trackNewRelicAction({
    appStateRef,
    actionProps: {
      action: NewRelicAnalyticsAction.TOKEN_401_AUTHORIZATION_ERROR,
      props: {
        url,
        method,
        isRegistered: debugInfo.isRegistered,
        isValid: debugInfo.isValid,
        hasScheduledRefresh: debugInfo.hasScheduledRefresh,
        timeToRefreshSeconds: debugInfo.timeToRefreshSeconds,
        timeToExpirationSeconds: debugInfo.tokenInfo?.timeToExpirationSeconds || -1,
        isExpired: debugInfo.tokenInfo?.isExpired || false,
      }
    }
  });

  if (!debugInfo.isValid) {
    ELSTokenServiceRegistrar.handleTokenExpired();
  }
};

const handleRequestError = (props: {
  appStateRef: React.MutableRefObject<AppState>;
  setAppState: AppContextState['setAppState'];
  error: Error;
  action: string;
  isTrackRequestCount: boolean;
}): Promise<never> => {

  const {
    error,
    action,
    isTrackRequestCount,
  } = props;

  const url = get(error, 'config.url', '');
  const method = get(error, 'config.method', '');
  const status = get(error, 'response.status', '');

  if (status === 400) {
    // Likely 400 error here due to overloaded headers
    removeCookies();
  }

  if (status === 401) {
    handle401Error({ appStateRef: props.appStateRef, url, method });
  }

  trackNewRelicAction({
    appStateRef: props.appStateRef,
    actionProps: {
      action: NewRelicAnalyticsAction.API_ERROR,
      props: {
        status,
        action,
        url,
        method
      }
    }
  });

  if (isTrackRequestCount) {
    requestEnd(props);
  }

  return Promise.reject(error);
};
export const fetchEvolveUsersAction = (props: {
  useCache: boolean;
  appStateRef: React.MutableRefObject<AppState>;
  setAppState: AppContextState['setAppState'];
  userId: string;
}): Promise<CrosswalkUserDto> => {
  const {
    useCache,
    appStateRef,
    setAppState,
    userId
  } = props;

  if (useCache && appStateRef.current.evolveUser) {
    return Promise.resolve(appStateRef.current.evolveUser);
  }

  // Set status to LOADING when starting the API call
  setAppState((prevState): AppState => ({
    ...prevState,
    crosswalkApiStatus: ApiStatus.LOADING
  }));

  requestStart({ setAppState });
  return fetchCrosswalkUser(userId, SystemType.EVOLVETYPE)
    .then((crosswalkUser) => {
      setAppState((prevState): AppState => {
        return {
          ...prevState,
          evolveUser: crosswalkUser,
          crosswalkApiStatus: ApiStatus.SUCCESS
        };
      });
      requestEnd(props);
      return crosswalkUser;
    })
    .catch((e) => {
      const status = get(e, 'response.status', '');

      // Handle 404 specifically - user has no Evolve account
      if (status === 404) {
        setAppState((prevState): AppState => ({
          ...prevState,
          evolveUser: null,
          crosswalkApiStatus: ApiStatus.NOT_FOUND
        }));
        requestEnd(props);
        return null;
      }

      // For other errors, set status to ERROR
      setAppState((prevState): AppState => ({
        ...prevState,
        crosswalkApiStatus: ApiStatus.ERROR
      }));

      return handleRequestError({
        appStateRef,
        setAppState,
        error: e,
        action: 'fetchCrosswalkUser',
        isTrackRequestCount: true
      });
    });
};

export const fetchAppLinkDataAction = (props: {
  appLinkId: string;
  appStateRef: React.MutableRefObject<AppState>;
  setAppState: AppContextState['setAppState'];
}): Promise<AppLinkData> => {
  const {
    appLinkId,
    setAppState,
    appStateRef
  } = props;

  requestStart({ setAppState });
  return fetchAppLinkData(appLinkId)
    .then((appLinkData) => {
      setAppState((prevState): AppState => {
        return {
          ...prevState,
          appLinkData,
          pendingRequestCount: prevState.pendingRequestCount - 1
        };
      });
      return appLinkData;
    })
    .catch((e) => handleRequestError({
      appStateRef,
      setAppState,
      error: e,
      action: 'fetchAppLinkDataAction',
      isTrackRequestCount: true
    }));
};

export const fetchHashLinkAction = (props: {
  linkHash: string;
  appStateRef: React.MutableRefObject<AppState>;
  setAppState: AppContextState['setAppState'];
}): Promise<HashLinkDto> => {
  const {
    linkHash,
    appStateRef,
    setAppState,
  } = props;

  requestStart({ setAppState });
  return fetchHashLink(linkHash)
    .then((hashLinkDto) => {
      setAppState((prevState): AppState => {
        return {
          ...prevState,
          appLinkCookies: hashLinkDto,
          pendingRequestCount: prevState.pendingRequestCount - 1
        };
      });
      return hashLinkDto;
    })
    .catch((e) => handleRequestError({
      appStateRef,
      setAppState,
      error: e,
      action: 'fetchHashLinkAction',
      isTrackRequestCount: true
    }));

};

const postGroupFeatureFlagAction = (props: {
  eolsApp: string;
  featureName: string;
  group: string;
  featureValue: string;
}): Promise<FeatureFlagDto> => {
  const {
    eolsApp,
    featureName,
    group,
    featureValue
  } = props;
  return postGroupFeatureFlag(eolsApp, featureName, group, featureValue).then(() => {
    return {
      createdAt: null,
      eolsApp,
      featureName,
      featureValue,
      group,
      updatedAt: null
    };
  });
};

const fetchABFlavorAction = (props: {
  appStateRef: React.MutableRefObject<AppState>;
  setAppState: AppContextState['setAppState'];
  abTestFlag: FeatureFlagsGroupedDto;
}): Promise<FeatureFlagDto> => {
  const {
    appStateRef,
    setAppState,
    abTestFlag
  } = props;
  const abTestFlavor = appStateRef.current.abTestFlavors.find((flag) => {
    return flag.featureName === abTestFlag.featureName;
  });
  if (abTestFlavor) {
    return Promise.resolve(abTestFlavor);
  }
  requestStart({ setAppState });
  const app = `${AppConstants.FEATURE_FLAG_APP}_${abTestFlag.featureName}`;
  const key = abTestFlag.featureName;
  return fetchGroupFeatureFlag(app, key, appStateRef.current.courseSectionId)
    .catch((error) => {
      if (error.response && error.response.status === 404) {
        const newFlavor = getRandomABTestFlavor(abTestFlag);
        if (!newFlavor) {
          ELSLoggingService.error(fileName, `AB test config missing for flag: ${key}`);
          return Promise.resolve({
            createdAt: null,
            eolsApp: app,
            featureName: key,
            featureValue: null,
            group: appStateRef.current.courseSectionId,
            updatedAt: null
          });
        }
        return postGroupFeatureFlagAction({
          eolsApp: app,
          featureName: key,
          group: appStateRef.current.courseSectionId,
          featureValue: newFlavor
        });
      }
      return Promise.reject(error);
    })
    .then((response) => {
      setAppState((prevState): AppState => {
        return {
          ...prevState,
          pendingRequestCount: prevState.pendingRequestCount - 1,
          abTestFlavors: [
            ...prevState.abTestFlavors,
            response
          ]
        };
      });
      return response;
    })
    .catch((e) => handleRequestError({
      appStateRef,
      setAppState,
      error: e,
      action: 'fetchABFlavor',
      isTrackRequestCount: true

    }));
};


export const fetchAllAppFeatureFlagsAction = (props: {
  appStateRef: React.MutableRefObject<AppState>;
  setAppState: AppContextState['setAppState'];
  useCache: boolean;
}): Promise<FeatureFlagsGroupedDto[]> => {

  const { setAppState, appStateRef } = props;

  if (props.useCache && appStateRef.current.featureFlagsGrouped) {
    return Promise.resolve(appStateRef.current.featureFlagsGrouped);
  }
  requestStart({ setAppState });
  return fetchGroupedFeatureFlags().then((response) => {

    setAppState((prevState): AppState => {
      return {
        ...prevState,
        featureFlagsGrouped: response,
        pendingRequestCount: prevState.pendingRequestCount - 1
      };
    });
    const promises = [];
    const activeABTests = getActiveABTestFlags(response, appStateRef.current.courseSectionId);

    if (activeABTests?.length) {
      activeABTests.forEach((activeABTest) => {
        promises.push(fetchABFlavorAction({
          abTestFlag: activeABTest,
          appStateRef,
          setAppState
        }));
      });
    }

    if (promises.length) {
      return Promise.all(promises).then(() => {
        return response;
      });
    }

    return response;
  })
    .catch((e) => handleRequestError({
      appStateRef,
      setAppState,
      error: e,
      action: 'fetchAllAppFeatureFlagsAction',
      isTrackRequestCount: true

    }));
};

export const fetchCourseSectionAction = (props: {
  appStateRef: React.MutableRefObject<AppState>;
  setAppState: AppContextState['setAppState'];
  courseSectionId: string;
  useCache: boolean;
}): Promise<CourseSectionDto> => {
  const {
    appStateRef,
    setAppState,
    courseSectionId,
    useCache
  } = props;
  if (!courseSectionId) {
    return Promise.resolve(null);
  }
  if (useCache && appStateRef.current.currentCourse) {
    return Promise.resolve(appStateRef.current.currentCourse);
  }
  requestStart({ setAppState });
  return fetchCourseSectionService(courseSectionId)
    .then((response) => {
      setAppState((prevState): AppState => {
        return {
          ...prevState,
          currentCourse: response,
          pendingRequestCount: prevState.pendingRequestCount - 1
        };
      });
      return response;
    })
    .catch((e) => handleRequestError({
      appStateRef,
      setAppState,
      error: e,
      action: 'fetchCourseSectionAction',
      isTrackRequestCount: true
    }));
};

export const navigateToApp = (props: AppContextState & {
  config: AppLinkOutConfig;
  isNewTab?: boolean;
  token?: string;
}): Promise<void> => {
  const {
    appStateRef,
    config,
    isNewTab,
    token
  } = props;
  requestStart(props);
  return getExternalAppRedirectUrl(config, token)
    .then(({ redirectUrl, linkId }) => {

      trackNewRelicAction({
        appStateRef,
        actionProps: {
          action: NewRelicAnalyticsAction.APP_LINK_OUT,
          props: {
            linkType: config.action,
            srcApp: config.altSrcApp || Application.NHE_AI,
            targetApp: config.app
          }
        }
      });

      setAppLinkCookies(linkId)

      if (isNewTab) {
        window.open(redirectUrl, '_blank');
      } else {
        window.location.href = redirectUrl;
      }
      requestEnd(props);
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'navigateToApp',
      isTrackRequestCount: true
    }));
};
export const returnAppLinkAction = (props: AppContextState & {
  linkId: string;
  returnPostBody: unknown;
}): Promise<void> => {
  const {
    linkId,
    returnPostBody,
  } = props;
  requestStart(props);
  return getReturnAppLink({ linkId, returnPostBody })
    .then(({ redirectUrl, linkId: responseLinkId }) => {
      setAppLinkCookies(responseLinkId);
      requestEnd(props);
      window.location.href = redirectUrl;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'returnAppLinkAction',
      isTrackRequestCount: true
    }));
};

export const fetchOsmosisTokenAction = (
  props: AppContextState
): Promise<OsmosisTokenDto> => {
  const token = props.appStateRef.current.osmosisTokenDto;
  if (!shouldGetNewOsmosisToken(token)) {
    return Promise.resolve(token);
  }
  requestStart(props);
  return fetchOsmosisToken()
    .then((response) => {
      props.setAppState((prevState): AppState => {
        return {
          ...prevState,
          osmosisTokenDto: response
        };
      });
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchOsmosisTokenAction',
      isTrackRequestCount: true
    }));
};

export const fetchEolsUserAction = (props: AppContextState & {
  userId: string;
  useCache: boolean;
}): Promise<UserDto> => {
  if (!props.userId) {
    return Promise.resolve(null);
  }

  if (props.useCache && props.appStateRef.current.eolsUser && props.appStateRef.current.eolsUser.id.toString() === props.userId) {
    return Promise.resolve(props.appStateRef.current.eolsUser);
  }

  requestStart(props);
  return fetchUser(props.userId)
    .then((user) => {
      // Strip courseSections to reduce storage footprint
      // The courseSections array is not used in the app - we use allUserCourseSections instead
      const userWithoutCourseSections = {
        ...user,
        courseSections: []
      };

      props.setAppState((prevState): AppState => {
        return {
          ...prevState,
          eolsUser: userWithoutCourseSections
        };
      });
      requestEnd(props);
      return userWithoutCourseSections;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchUserAction',
      isTrackRequestCount: true
    }));
};

export const fetchChatsAction = (props: AppContextState & {
  contextId: string;
  contextType: string;
  pageSize: number;
  page: number;
  useCache: boolean;
  userRole: ChatUserRoleDto;
  context: ContextRequestDto[];
}): Promise<ChatDto[]> => {
  if (props.useCache && props.appStateRef.current.chats) {
    return Promise.resolve(props.appStateRef.current.chats);
  }
  requestStart(props);
  return fetchChats(props)
    .then((response) => {
      requestEnd(props);
      return response.content;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchChatsAction',
      isTrackRequestCount: true
    }));
};

export const fetchChatAction = (props: AppContextState & {
  chatId: number;
}): Promise<ChatDto> => {
  requestStart(props);
  return fetchChat(props.chatId)
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchChatAction',
      isTrackRequestCount: true
    }));
};

export const fetchChatEntitlementsAction = (
  props: AppContextState & {
    useCache: boolean;
    isOsmosis: boolean;
  }
): Promise<
  Partial<
    Record<
      ChatEntitlementTypeDto,
      ChatEntitlementDto[]
    >
  >
> => {
  if (props.useCache && props.appStateRef.current.entitlements) {
    return Promise.resolve(props.appStateRef.current.entitlements);
  }
  requestStart(props);
  return fetchChatEntitlements(props.isOsmosis, props.appState)
    .then((chats) => {
      requestEnd(props);
      return chats;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchChatEntitlementsAction',
      isTrackRequestCount: true
    }));
};

export const postChatPromptAction = (props: AppContextState & {
  entry: ShellsDto;
  chatId: number;
}): Promise<ChatEntryDto> => {
  requestStart(props);
  return postChatPrompt(props.entry, props.chatId)
    .then((chats) => {
      requestEnd(props);
      return chats;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'postChatPromptAction',
      isTrackRequestCount: true
    }));
};

export const postChatEntryShellsAction = (props: AppContextState & {
  entry: ShellsDto;
  chatId: number;
}): Promise<ChatEntryDto[]> => {
  requestStart(props);
  return postChatEntryShells(props.entry, props.chatId)
    .then((chats) => {
      requestEnd(props);
      return chats;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'postChatEntryShellsAction',
      isTrackRequestCount: true
    }));
};

export const fetchChatEntryAction = (props: AppContextState & {
  entryId: number;
  chatId: number;
}): Promise<ChatEntryDto> => {
  requestStart(props);
  return fetchChatEntry(props.chatId, props.entryId)
    .then((chats) => {
      requestEnd(props);
      return chats;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchChatEntryAction',
      isTrackRequestCount: true
    }));
};


export const putFollowUpQuestionAction = (props: AppContextState & {
  followUpQuestion: FollowUpQuestionDto;
}): Promise<string[]> => {
  requestStart(props);
  return putFollowUpQuestion(props.followUpQuestion)
    .then((chats) => {
      requestEnd(props);
      return chats;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'putFollowUpQuestionAction',
      isTrackRequestCount: true
    }));
};

export const patchFollowUpQuestionAction = (props: AppContextState & {
  followUpQuestionId: number;
}): void => {
  requestStart(props);
  patchFollowUpQuestion(props.followUpQuestionId)
    .then(() => {
      requestEnd(props);
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'patchFollowUpQuestionAction',
      isTrackRequestCount: true
    }));
};

export const postChatAction = (props: AppContextState & {
  chat: Omit<ChatDto, 'id'>;
}): Promise<ChatDto> => {
  requestStart(props);
  return postChat(props.chat)
    .then((chats) => {
      requestEnd(props);
      return chats;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'postChatAction',
      isTrackRequestCount: true
    }));
};
export const deleteChatAction = (props: AppContextState & {
  id: number;
}): Promise<void> => {
  requestStart(props);
  return deleteChat(props.id)
    .then((chats) => {
      requestEnd(props);
      return chats;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'deleteChatAction',
      isTrackRequestCount: true
    }));
};

export const putChatAction = (props: AppContextState & {
  chat: ChatDto;
}): Promise<ChatDto> => {
  requestStart(props);
  return putChat(props.chat)
    .then((chats) => {
      requestEnd(props);
      return chats;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'putChatAction',
      isTrackRequestCount: true
    }));
};

export const fetchChatEvaluationsAction = (props: AppContextState & {
  chatId: number;
}): Promise<ChatEntryEvaluationDto[]> => {
  requestStart(props);
  return fetchChatEvaluations(props.chatId)
    .then((chats) => {
      requestEnd(props);
      return chats;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchChatEvaluationsAction',
      isTrackRequestCount: true
    }));
};

export const fetchChatEntriesAction = (props: AppContextState & {
  chatId: number;
  page: number;
  pageSize: number;
}): Promise<ChatPaginationResponseDto & {
  content: ChatEntryDto[];
}> => {
  requestStart(props);
  return fetchChatEntries(
    props.chatId,
    props.page,
    props.pageSize
  )
    .then((chats) => {
      requestEnd(props);
      return chats;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchChatEntriesAction',
      isTrackRequestCount: true
    }));
};

export const fetchFollowUpQuestionsAction = (props: AppContextState & {
  botEntryId: number;
}): Promise<FollowUpQuestionDto[]> => {
  requestStart(props);
  return fetchFollowUpQuestions(props.botEntryId)
    .then((followUpQuestions) => {
      requestEnd(props);
      return followUpQuestions;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchFollowUpQuestionsAction',
      isTrackRequestCount: true
    }));
};

export const openVitalSourceAction = (props: AppContextState & {
  isbn: string;
  page: number;
}): Promise<Window> => {
  requestStart(props);
  return fetchVitalSource({
    isbn: props.isbn,
    page: props.page,
  })
    .then(vitalSource => {
      requestEnd(props);
      return window.open(vitalSource.signInUrl, '_blank');
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'openVitalSource',
      isTrackRequestCount: true
    }));
};

export const fetchEbookSsoUrlAction = (props: AppContextState & {
  request: EbookSsoUrlRequestDto;
}): Promise<EbookSsoUrlResponseDto> => {
  requestStart(props);
  return fetchEbookSsoUrl(props.request)
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchEbookSsoUrlAction',
      isTrackRequestCount: true
    }));
};

/**
 * Resolves to null when NACS reports no content for the CFI. That is an expected
 * outcome the caller falls back from, not an error, so it resolves normally rather
 * than going through handleRequestError.
 *
 * A genuine request failure is not swallowed: handleRequestError fires API_ERROR,
 * closes out the request counter, and then rethrows (Promise<never>), so the
 * rejection still reaches the caller, which falls back to the page-range request.
 */
export const fetchEbookCfiSsoUrlAction = (props: AppContextState & {
  request: EbookCfiSsoUrlRequestDto;
}): Promise<EbookSsoUrlResponseDto | null> => {
  requestStart(props);
  return fetchEbookCfiSsoUrl(props.request)
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchEbookCfiSsoUrlAction',
      isTrackRequestCount: true
    }));
};

export const fetchChatEeoIsbnsAction = (props: AppContextState): Promise<string[]> => {
  requestStart(props);
  return fetchChatEeoIsbns()
    .then((vitalSource) => {
      requestEnd(props);
      return vitalSource;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchChatEeoIsbnsAction',
      isTrackRequestCount: true
    }));
};

export const adminFetchChatEntryAction = (props: AppContextState & {
  entryId: string;
}): Promise<ChatEntryDto> => {
  requestStart(props);
  return adminFetchChatEntry(props.entryId)
    .then((chatEntry) => {
      requestEnd(props);
      return chatEntry;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'adminFetchChatEntryAction',
      isTrackRequestCount: true
    }));
};

export const adminFetchChatAction = (props: AppContextState & {
  chatId: string;
}): Promise<ChatDto> => {
  requestStart(props);
  return adminFetchChat(props.chatId)
    .then((chatEntry) => {
      requestEnd(props);
      return chatEntry;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'adminFetchChatAction',
      isTrackRequestCount: true
    }));
};

export const adminFetchChatEntriesAction = (props: AppContextState & {
  chatId: string;
  page: number;
  pageSize: number;
}): Promise<ChatPaginationResponseDto & {
  content: ChatEntryDto[];
}> => {
  requestStart(props);
  return adminFetchChatEntries(props.chatId, props.page, props.pageSize)
    .then((chats) => {
      requestEnd(props);
      return chats;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'adminFetchChatEntriesAction',
      isTrackRequestCount: true
    }));
};

// fetchChatPerformanceAction
export const fetchChatPerformanceAction = (props: AppContextState & {
  startDate: string;
  numOfDays: number;
  courseSectionIds: number[];
}): Promise<ChatPerformanceDto> => {
  requestStart(props);
  return fetchChatPerformance(
    props.startDate,
    props.numOfDays,
    props.courseSectionIds
  )
    .then((evaluations) => {
      requestEnd(props);
      return evaluations;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchChatPerformanceAction',
      isTrackRequestCount: true
    }));
};

// fetchStudentThemesStatusAction
export const fetchStudentThemesStatusAction = (props: AppContextState & {
  startDate: string;
  numOfDays: number;
  courseSectionIds: number[];
}): Promise<StudentThemesStatusDto> => {
  return fetchStudentThemesStatus(
    props.startDate,
    props.numOfDays,
    props.courseSectionIds
  )
    .then((status) => {
      return status;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchStudentThemesStatusAction',
      isTrackRequestCount: false
    }));
};

// fetchUserCourseSectionsAction
export const fetchUserCourseSectionsAction = (props: AppContextState & {
  userId: string;
  active?: boolean;
  entitled?: boolean;
}): Promise<CourseSectionDto[]> => {
  requestStart(props);
  return fetchUserCourseSections(
    props.userId,
    props.active,
    props.entitled
  )
    .then((evaluations) => {
      requestEnd(props);
      return evaluations;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchUserCourseSectionsAction',
      isTrackRequestCount: true
    }));
};

export const fetchAllUserCourseSectionsAction = (props: AppContextState): Promise<CourseSectionDto[]> => {

  requestStart(props);
  return fetchEvolveUsersAction({
    ...props,
    userId: props.appStateRef.current.userId,
    useCache: true
  })
    .then((crossWalkUser) => {
      // If no Evolve user (404), fetch course sections for current EOLS user only
      if (!crossWalkUser) {
        return Promise.all([
          fetchUserCourseSectionsAction({
            ...props,
            userId: props.appStateRef.current.userId
          })
        ]);
      }

      // If Evolve user exists, fetch for all linked EOLS users
      return Promise.all(crossWalkUser.eolsUserIdsByUserNameAndType.map((_userId) => {
        return fetchUserCourseSectionsAction({
          ...props,
          userId: _userId.toString()
        });
      }));
    })
    .then((responses) => {
      return responses.reduce((acc, courseSections) => {
        return [
          ...acc,
          ...courseSections
        ];
      }, []);
    })
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchAllUserCourseSectionsAction',
      isTrackRequestCount: true
    }));
};

// postStudentThemesAction
export const postStudentThemesAction = (props: AppContextState & {
  startDate: string;
  numOfDays: number;
  courseSectionIds: number[];
}): Promise<void> => {
  requestStart(props);
  return postStudentThemes(
    props.startDate,
    props.numOfDays,
    props.courseSectionIds
  )
    .then((studentThemes) => {
      requestEnd(props);
      return studentThemes;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'postStudentThemesAction',
      isTrackRequestCount: true
    }));
};

// fetchAdminChatEeoIsbnsAction
export const fetchAdminChatEeoIsbnsAction = (props: AppContextState & {
  isbns: string[];
}): Promise<AdminChatIsbnBookDto[]> => {
  requestStart(props);
  return fetchAdminChatEeoIsbns(props.isbns)
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchAdminChatEeoIsbnsAction',
      isTrackRequestCount: true
    }));
};

export const adminSearchVectorAction = (props: AppContextState & {
  vectorDto: VectorSearchDto;
}): Promise<VectorSearchResponseDto> => {
  requestStart(props);
  return adminSearchVector(props.vectorDto)
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'adminSearchVectorAction',
      isTrackRequestCount: true
    }));
};

export const adminPromptAction = (props: AppContextState & {
  llmPromptDto: LlmPromptDto;
}): Promise<LlmResponseDto> => {
  requestStart(props);
  return adminPrompt(props.llmPromptDto)
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'adminPromptAction',
      isTrackRequestCount: true
    }));
};

export const handleChatUpdateAction = (props: AppContextState & {
  chat: ChatDto;
}): Promise<ChatDto> => {
  const {
    appState,
    appStateRef,
    setAppState,
    chat
  } = props;
  return putChatAction({
    appState,
    appStateRef,
    setAppState,
    chat
  }).then((response) => {
    setAppState((prevState) => {
      return {
        ...prevState,
        chats: prevState.chats ? prevState.chats.map((_chat) => {
          if (_chat.id === chat.id) {
            return response;
          }
          return _chat;
        }) : prevState.chats
      };
    });
    return response;
  });
};

export const handleChatClickAction = (props: AppContextState & {
  chat: ChatDto;
  sendClickEvent: boolean;
  checkLoading: boolean;
  toastService: ELSToastService;
}): void => {

  const {
    appStateRef,
    setAppState,
    chat,
    toastService,
    sendClickEvent,
    checkLoading
  } = props;

  if (!chat) {
    return;
  }

  const currentActiveChatId = appStateRef.current.activeChatId;

  if (currentActiveChatId === chat.id) {
    return;
  }

  if (sendClickEvent) {
    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_CHAT_CLICK,
        props: {
          targetChatId: chat.id,
          targetChatTitle: chat.title,
          isTargetActive: chat.id === currentActiveChatId,
          isStreaming: appStateRef.current.isLoading,
          chatUserRole: appStateRef.current.chatUserRole
        }
      }
    });
  }

  if (checkLoading && isInputBlocked(appStateRef.current)) {
    toastActiveStreamingError({
      appState: appStateRef.current,
      appStateRef,
      setAppState,
      toastService,
      trackNewRelicAction,
      actionRef: 'CHAT_CLICK'
    });
    return;
  }

  setAppState((prevState) => {
    return {
      ...prevState,
      activeChatId: chat.id,
      showLoader: true
    };
  });

};

export const handleChatCreateAction = (props: AppContextState & {
  chat: Omit<ChatDto, 'id'>;
}): Promise<ChatDto> => {

  const {
    appState,
    appStateRef,
    setAppState,
    chat,
  } = props;

  return postChatAction({
    appState,
    appStateRef,
    setAppState,
    chat
  }).then((response) => {

    setAppState((prevState) => {
      return {
        ...prevState,
        activeChatId: response.id,
        showLoader: false,
        chats: prevState.chats ? [
          ...prevState.chats,
          response
        ] : [response],
      }
    })

    return response;
  });
};

export const fetchIpAddressAction = (props: AppContextState): Promise<string> => {
  if (props.appStateRef.current.ipAddress) {
    return Promise.resolve(props.appStateRef.current.ipAddress);
  }
  requestStart(props);
  return fetchIpAddress()
    .then((response) => {
      props.setAppState((prevState) => {
        return {
          ...prevState,
          ipAddress: response.data.ip
        };
      });
      requestEnd(props);
      return response.data.ip;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchIpAddressAction',
      isTrackRequestCount: true
    }));
};

export const fetchOsmosisLearnDataAction = (props: AppContextState & {
  learnNodeId: string;
}): Promise<OsmosisLearnData> => {
  requestStart(props);
  return fetchOsmosisLearnData(props.learnNodeId)
    .then((response) => {
      requestEnd(props);
      return response.data;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchOsmosisLearnDataAction',
      isTrackRequestCount: true
    }));
};

export const putReferenceVideoDataAction = (props: AppContextState & {
  referenceId: number;
}): Promise<ChatReferenceDto> => {
  requestStart(props);
  return putReferenceVideoData(props.referenceId)
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'putReferenceVideoDataAction',
      isTrackRequestCount: true
    }));
};

export const fetchRelatedContentAction = (props: AppContextState & {
  botEntryId: number;
  setChatState: React.Dispatch<React.SetStateAction<{ chatEntries: ChatEntryDto[] }>>;
}): Promise<RelatedContentDto[]> => {
  requestStart(props);
  return fetchRelatedContent(props.botEntryId)
    .then((response) => {
      requestEnd(props);
      props.setChatState((prevState) => {
        const updatedEntries = prevState.chatEntries?.map((entry) => {
          if (entry.id !== props.botEntryId) {
            return entry;
          }
          return {
            ...entry,
            relatedContent: response,
          };
        });
        return {
          ...prevState,
          chatEntries: updatedEntries,
        };
      });
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchRelatedContentAction',
      isTrackRequestCount: true
    }));
};

export const fetchFlashcardsAction = (props: AppContextState & {
  botEntryId: number;
}): Promise<FlashcardResponseDto> => {
  requestStart(props);
  return fetchFlashcards(props.botEntryId)
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchFlashcardsAction',
      isTrackRequestCount: true
    }));
};

export const fetchUserHistoryByCourseByStateKeyAction = (props: AppContextState & {
  userId: number;
  stateKey: CoursewareUserHistoryStateKey | string;
  courseSectionId: number;
  instanceId?: string;
}): Promise<EolsUserHistoryResponseDto[]> => {
  requestStart(props);
  return fetchUserHistoryByCourseByStateKey({
    userId: props.userId,
    stateKey: props.stateKey,
    courseSectionId: props.courseSectionId,
    instanceId: props.instanceId,
  })
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchUserHistoryByCourseByStateKeyAction',
      isTrackRequestCount: true
    }));
};

export const postUserHistoryAction = (props: AppContextState & {
  eolsUserHistoryDTO: EolsUserHistoryDto;
}): Promise<EolsUserHistoryResponseDto> => {
  requestStart(props);
  return postUserHistory(props.eolsUserHistoryDTO)
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'postUserHistoryAction',
      isTrackRequestCount: true
    }));
};

export const deleteUserHistoryByUserIdAction = (props: AppContextState & {
  userId: number;
  instanceId?: string;
}): Promise<unknown> => {
  requestStart(props);
  return deleteUserHistoryByUserId({
    userId: props.userId,
    instanceId: props.instanceId,
  })
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'deleteUserHistoryByUserIdAction',
      isTrackRequestCount: true
    }));
};

export const fetchPostProcessJobsAction = (props: AppContextState & {
  chatId?: number;
  entryId?: number;
  jobType?: PostProcessJobTypeDto;
}): Promise<PostProcessJobDto[]> => {
  requestStart(props);
  return fetchPostProcessJobs({
    chatId: props.chatId,
    entryId: props.entryId,
    jobType: props.jobType,
  })
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchPostProcessJobsAction',
      isTrackRequestCount: true
    }));
};

export const fetchGeneratedQuizQuestionsAction = (props: AppContextState & {
  botEntryId: number;
}): Promise<GeneratedQuizQuestionDto[]> => {
  requestStart(props);
  return fetchGeneratedQuizQuestions(props.botEntryId)
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchGeneratedQuizQuestionsAction',
      isTrackRequestCount: true
    }));
};

export const fetchQuizQuestionsAction = (props: AppContextState & {
  params: QuizQuestionQueryParamsDto;
  userRole: ChatUserRoleDto;
  context: ContextRequestDto[];
}): Promise<QuizQuestionListResponseDto> => {
  requestStart(props);
  return fetchQuizQuestions({ params: props.params, userRole: props.userRole, context: props.context })
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'fetchQuizQuestionsAction',
      isTrackRequestCount: true
    }));
};

export const createPracticeQuizAction = (props: AppContextState & {
  request: CreatePracticeQuizRequestDto;
  userRole: ChatUserRoleDto;
  context: ContextRequestDto[];
}): Promise<CreatePracticeQuizResponseDto> => {
  requestStart(props);
  return createPracticeQuiz({ request: props.request, userRole: props.userRole, context: props.context })
    .then((response) => {
      requestEnd(props);
      return response;
    })
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'createPracticeQuizAction',
      isTrackRequestCount: true
    }));
};

export const putRefreshTokenAction = (props: AppContextState & {
  refreshToken: string;
}): Promise<TokenRotationResponse> => {
  return putRefreshToken(props.refreshToken)
    .catch((e) => handleRequestError({
      ...props,
      error: e,
      action: 'putRefreshTokenAction',
      isTrackRequestCount: false
    }));
};
